import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { T, Var, useGT } from 'gt-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SingleSelect } from '@/components/common/SingleSelect';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useProfitabilityReport,
  useUploadProfitabilityData,
  useValidateProfitabilityData,
  useProductFamilyDescriptions,
} from '@/api/rebates/useReports';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import useDivisions from '@/api/organization/useDivisions';
import useAllSuppliers from '@/api/accounts-payable/useAllSuppliersQuery';
import type {
  SupplierSalesMarginUploadResponse,
  SupplierSalesMarginValidateResponse,
  ProfitabilityReportRow,
  ProfitabilityDimension,
  UnmatchedBreakdown,
  ReportLineType,
} from '@erp/shared-types';
import {
  LINE_TYPE_LABELS,
  LINE_TYPE_DESCRIPTIONS,
} from '@/types/rebateAnalytics';
import { groupSupplierRows } from './supplierGroups';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import FileUploadDropzone from '@/components/documents/FileUploadDropzone';
import { UploadProgress, UploadStatus } from 'src/types/UserDocuments';
import {
  UploadIcon,
  CircleNotchIcon,
  TrashIcon,
  FileCsvIcon,
  FileXlsIcon,
  CaretDownIcon,
  ShareNetworkIcon,
  LinkIcon,
  WarningIcon,
  InfoIcon,
  DotsSixVerticalIcon,
  ArrowsOutIcon,
  XIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  downloadProfitabilityReportCsv,
  fetchProfitabilityReportCsv,
} from '@/api/rebates/useRebateDataExports';
import { downloadRebateCsvStringXlsx } from '@/components/rebates/reports/export/downloadRebateReportXlsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import { useToast } from 'src/context/ToastContext';
import {
  TreeTable,
  type ColumnDef,
} from '@/components/common/tree-table/TreeTable';
import { formatCurrencyAdvanced } from '@erp/shared-utils';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { deleteSalesMarginsForYear } from '@/api/rebates/useRebateDataExports';
import {
  AlertContainer,
  AlertTitle,
  AlertDescription,
} from '@/components/ui/alert';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { HeaderPageTitle } from 'src/context/header-title.context';

const REPORT_ID = 'profitability-report';
const REPORT_TITLE = 'Profitability Report';

type DimensionTuple = [
  ProfitabilityDimension,
  ProfitabilityDimension,
  ProfitabilityDimension,
];

const DIMENSION_LABELS: Record<ProfitabilityDimension, string> = {
  division: 'Division',
  supplier: 'Supplier',
  productFamily: 'Product Family',
};

/**
 * Draggable chip for dimension reordering
 */
const SortableDimensionChip: React.FC<{ id: ProfitabilityDimension }> = ({
  id,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 text-sm font-medium select-none cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-50 shadow-lg z-10' : ''
      }`}
      {...attributes}
      {...listeners}
    >
      <DotsSixVerticalIcon
        size={14}
        className="text-muted-foreground shrink-0"
      />
      {DIMENSION_LABELS[id]}
    </div>
  );
};

// Format currency with 0 decimal places for whole dollar amounts
const formatCurrencyWhole = (value: number): string => {
  return formatCurrencyAdvanced(value, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatPercent = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

/**
 * Format date range from YYYY-MM format to readable month range
 */
const formatDateRange = (
  minYearMonth: string,
  maxYearMonth: string,
): string => {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const parseYearMonth = (
    yearMonth: string,
  ): { year: number; month: number } => {
    const [year, month] = yearMonth.split('-').map(Number);
    return { year, month };
  };

  const min = parseYearMonth(minYearMonth);
  const max = parseYearMonth(maxYearMonth);

  const minMonthName = monthNames[min.month - 1];
  const maxMonthName = monthNames[max.month - 1];

  if (min.year === max.year) {
    return `${minMonthName} - ${maxMonthName}`;
  }

  return `${minMonthName} ${min.year} - ${maxMonthName} ${max.year}`;
};

const REASON_LABELS: Record<string, { label: string; description: string }> = {
  no_sales_margin: {
    label: 'No Sales Data',
    description:
      'These suppliers have rebate agreements but no sales margin data has been uploaded. Upload sales data for these suppliers to include their rebates in the report.',
  },
  no_matching_families: {
    label: 'Untagged Product Groups',
    description:
      'Some product groups on these rules are not tagged with a product family. Tag the product groups in the agreement to improve accuracy.',
  },
  no_hierarchy_node: {
    label: 'Rounding',
    description:
      'Small amounts from floating-point rounding during distribution.',
  },
  zero_cogs: {
    label: 'Zero COGS',
    description:
      'The supplier/division combination has zero cost of goods sold.',
  },
};

interface ReasonGroup {
  reason: string;
  label: string;
  description: string;
  totalAmount: number;
  suppliers: Array<{
    supplierId: string;
    supplierName: string;
    divisions: Array<{
      divisionId: string;
      divisionName: string;
      amount: number;
    }>;
    totalAmount: number;
  }>;
}

/**
 * Data Quality Notice component — groups unmatched breakdowns by reason
 */
const DataQualityNotice: React.FC<{
  unmatchedBreakdowns: UnmatchedBreakdown[];
  isAdmin?: boolean;
}> = ({ unmatchedBreakdowns, isAdmin = false }) => {
  const gt = useGT();
  const [isOpen, setIsOpen] = React.useState(false);
  const [expandedReasons, setExpandedReasons] = React.useState<Set<string>>(
    new Set(),
  );

  const { reasonGroups, totalAmount } = React.useMemo(() => {
    // Group by reason → supplier → division
    const reasonMap = new Map<
      string,
      Map<
        string,
        {
          name: string;
          divisions: Map<string, { name: string; amount: number }>;
        }
      >
    >();
    let total = 0;

    for (const bd of unmatchedBreakdowns) {
      const amount = Math.abs(bd.rebateAmount);
      total += amount;

      if (!reasonMap.has(bd.reason)) {
        reasonMap.set(bd.reason, new Map());
      }
      let supplierMap = reasonMap.get(bd.reason);
      if (!supplierMap) {
        supplierMap = new Map();
        reasonMap.set(bd.reason, supplierMap);
      }

      if (!supplierMap.has(bd.supplierId)) {
        supplierMap.set(bd.supplierId, {
          name: bd.supplierName,
          divisions: new Map(),
        });
      }
      const supplier = supplierMap.get(bd.supplierId);
      if (!supplier) continue;

      const existing = supplier.divisions.get(bd.divisionId);
      if (existing) {
        existing.amount += amount;
      } else {
        supplier.divisions.set(bd.divisionId, {
          name: bd.divisionName,
          amount,
        });
      }
    }

    // Build sorted groups
    const groups: ReasonGroup[] = [];
    // Show in this order: no_sales_margin first (biggest), then no_matching_families, then rest
    const reasonOrder = [
      'no_sales_margin',
      'no_matching_families',
      'no_hierarchy_node',
      'zero_cogs',
    ];
    const sortedReasons = [...reasonMap.keys()].sort(
      (a, b) =>
        (reasonOrder.indexOf(a) === -1 ? 99 : reasonOrder.indexOf(a)) -
        (reasonOrder.indexOf(b) === -1 ? 99 : reasonOrder.indexOf(b)),
    );

    for (const reason of sortedReasons) {
      const supplierMap = reasonMap.get(reason);
      if (!supplierMap) continue;
      const meta = REASON_LABELS[reason] || {
        label: reason,
        description: '',
      };

      let reasonTotal = 0;
      const suppliers: ReasonGroup['suppliers'] = [];

      for (const [supplierId, supplier] of supplierMap) {
        const divisions = [...supplier.divisions.entries()]
          .map(([divisionId, div]) => ({
            divisionId,
            divisionName: div.name,
            amount: div.amount,
          }))
          .sort((a, b) => b.amount - a.amount);

        const supplierTotal = divisions.reduce((s, d) => s + d.amount, 0);
        reasonTotal += supplierTotal;

        suppliers.push({
          supplierId,
          supplierName: supplier.name,
          divisions,
          totalAmount: supplierTotal,
        });
      }

      suppliers.sort((a, b) => b.totalAmount - a.totalAmount);
      groups.push({
        reason,
        label: meta.label,
        description: meta.description,
        totalAmount: reasonTotal,
        suppliers,
      });
    }

    return { reasonGroups: groups, totalAmount: total };
  }, [unmatchedBreakdowns]);

  const toggleReason = (reason: string) => {
    setExpandedReasons((prev) => {
      const next = new Set(prev);
      if (next.has(reason)) {
        next.delete(reason);
      } else {
        next.add(reason);
      }
      return next;
    });
  };

  const supplierCount = new Set(unmatchedBreakdowns.map((b) => b.supplierId))
    .size;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center gap-3 w-full cursor-pointer rounded-lg border border-yellow-200 bg-yellow-50 hover:bg-yellow-100 transition-colors px-4 py-3">
          <WarningIcon className="h-4 w-4 text-yellow-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-yellow-800">
              <T>Unmatched Rebates</T>
            </div>
            <div className="text-sm text-yellow-700">
              <T>
                <Var>{formatCurrencyWhole(totalAmount)}</Var> across{' '}
                <Var>{supplierCount}</Var>{' '}
                <Var>{supplierCount === 1 ? 'supplier' : 'suppliers'}</Var>{' '}
                could not be distributed. Click to view details.
              </T>
            </div>
          </div>
          <CaretDownIcon
            className={`h-4 w-4 text-yellow-600 transition-transform flex-shrink-0 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 space-y-2">
          {reasonGroups
            .filter((group) => isAdmin || group.reason !== 'no_hierarchy_node')
            .map((group) => (
              <div
                key={group.reason}
                className="border border-yellow-200 rounded-md bg-white overflow-hidden"
              >
                <button
                  type="button"
                  className="flex items-center justify-between w-full px-4 py-2.5 bg-yellow-50 hover:bg-yellow-100 transition-colors text-left"
                  onClick={() => toggleReason(group.reason)}
                >
                  <div>
                    <span className="font-medium text-yellow-800">
                      {group.label}
                    </span>
                    <span className="text-sm text-yellow-600 ml-2">
                      {group.suppliers.length}{' '}
                      {group.suppliers.length === 1
                        ? gt('supplier', {
                            context:
                              'singular count label: 1 supplier in an unmatched rebate group',
                          })
                        : gt('suppliers', {
                            context:
                              'plural count label: multiple suppliers in an unmatched rebate group',
                          })}
                      , {formatCurrencyWhole(group.totalAmount)}
                    </span>
                  </div>
                  <CaretDownIcon
                    className={`h-3.5 w-3.5 text-yellow-600 transition-transform ${
                      expandedReasons.has(group.reason) ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedReasons.has(group.reason) && (
                  <div className="px-4 pb-3">
                    <p className="text-xs text-gray-500 py-2">
                      {group.description}
                    </p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">
                            <T>Supplier</T>
                          </TableHead>
                          <TableHead className="text-xs">
                            <T>Division</T>
                          </TableHead>
                          <TableHead className="text-xs text-right">
                            <T>Amount</T>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.suppliers.map((supplier) =>
                          supplier.divisions.map((div, idx) => (
                            <TableRow
                              key={`${supplier.supplierId}-${div.divisionId}`}
                              className="hover:bg-gray-50"
                            >
                              <TableCell className="text-sm font-medium">
                                {idx === 0 ? supplier.supplierName : ''}
                              </TableCell>
                              <TableCell className="text-sm">
                                {div.divisionName}
                              </TableCell>
                              <TableCell className="text-sm text-right">
                                {formatCurrencyWhole(div.amount)}
                              </TableCell>
                            </TableRow>
                          )),
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export interface ProfitabilityReportContentProps {
  isRestrictedUser?: boolean;
  urlYear?: number;
  urlMonth?: number | null;
  urlLineType?: ReportLineType;
  urlDimensions?: DimensionTuple;
  urlSortBy?: string;
  urlShowDollar?: boolean;
  onSearchParamsChange?: (updates: Record<string, unknown>) => void;
}

export const ProfitabilityReportContent: React.FC<
  ProfitabilityReportContentProps
> = ({
  isRestrictedUser = false,
  urlYear,
  urlMonth,
  urlLineType,
  urlDimensions,
  urlSortBy,
  urlShowDollar,
  onSearchParamsChange,
}) => {
  const gt = useGT();
  const { showToast } = useToast();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const initializedFromUrl = useRef(false);

  const [selectedYear, setSelectedYear] = useState<number>(
    urlYear ?? currentYear,
  );
  const [selectedMonth, setSelectedMonth] = useState<number | null>(
    urlMonth !== undefined ? urlMonth : null,
  );
  const [confirmedDimensions, setConfirmedDimensions] =
    useState<DimensionTuple>(
      urlDimensions ?? ['supplier', 'division', 'productFamily'],
    );
  const [pendingDimensions, setPendingDimensions] = useState<DimensionTuple>(
    urlDimensions ?? ['supplier', 'division', 'productFamily'],
  );
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedProductFamilyDescs, setSelectedProductFamilyDescs] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedShipTypes, setSelectedShipTypes] = useState<
    MultiSelectOption[]
  >([
    { id: '1', label: 'Ship Type 1', value: 1 },
    { id: '2', label: 'Ship Type 2', value: 2 },
    { id: '3', label: 'Ship Type 3', value: 3 },
  ]);
  const [selectedColumn, setSelectedColumn] = useState<string | null>(
    urlSortBy ?? null,
  );
  const [showDollarAmounts, setShowDollarAmounts] = useState(
    urlShowDollar ?? false,
  );
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [uploadErrors, setUploadErrors] = useState<
    Array<{ row: number; message: string }>
  >([]);
  const [validationResult, setValidationResult] =
    useState<SupplierSalesMarginValidateResponse | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadYear, setUploadYear] = useState<number>(urlYear ?? currentYear);
  const [selectedLineType, setSelectedLineType] = useState<ReportLineType>(
    urlLineType ?? 'reported',
  );
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [nameFilter, setNameFilter] = useState('');
  const debouncedNameFilter = useDebounce(nameFilter, 300);

  // Sync state changes to URL params
  useEffect(() => {
    // Skip the initial render to avoid overwriting URL with defaults
    if (!initializedFromUrl.current) {
      initializedFromUrl.current = true;
      return;
    }
    onSearchParamsChange?.({
      year: selectedYear,
      month: selectedMonth ?? undefined,
      lineType: selectedLineType === 'reported' ? undefined : selectedLineType,
      dimensions:
        confirmedDimensions.join(',') === 'supplier,division,productFamily'
          ? undefined
          : confirmedDimensions.join(','),
      sortBy: selectedColumn ?? undefined,
      showDollar: showDollarAmounts || undefined,
    });
  }, [
    selectedYear,
    selectedMonth,
    selectedLineType,
    confirmedDimensions,
    selectedColumn,
    showDollarAmounts,
    onSearchParamsChange,
  ]);

  // Sync uploadYear with selectedYear when it changes (when modal is closed)
  useEffect(() => {
    if (!isUploadModalOpen) {
      setUploadYear(selectedYear);
    }
  }, [selectedYear, isUploadModalOpen]);

  // Fetch divisions for filter
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  // Fetch suppliers for filter
  const { data: suppliersData, isLoading: suppliersLoading } = useAllSuppliers(
    undefined,
    true,
  );

  // Fetch product family descriptions for filter
  const { data: productFamilyDescriptions, isLoading: prodFamilyDescsLoading } =
    useProductFamilyDescriptions();

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Prepare year options - descending order, no future years
  const yearOptions = useMemo(() => {
    if (!availableYears || availableYears.length === 0) {
      const years: number[] = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years.map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
    }
    return availableYears
      .filter((year) => year <= currentYear)
      .sort((a, b) => b - a)
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears, currentYear]);

  // Prepare division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare supplier options
  const supplierOptions: MultiSelectOption[] = useMemo(() => {
    if (!suppliersData) return [];
    return suppliersData.map((supplier) => ({
      id: supplier.id,
      label: supplier.name,
      value: supplier,
    }));
  }, [suppliersData]);

  // Prepare product family description options
  const productFamilyDescOptions: MultiSelectOption[] = useMemo(() => {
    if (!productFamilyDescriptions) return [];
    return productFamilyDescriptions.map((desc) => ({
      id: desc,
      label: desc,
      value: desc,
    }));
  }, [productFamilyDescriptions]);

  // Ship type options
  const shipTypeOptions: MultiSelectOption[] = useMemo(() => {
    return [
      { id: '1', label: 'Ship Type 1', value: 1 },
      { id: '2', label: 'Ship Type 2', value: 2 },
      { id: '3', label: 'Ship Type 3', value: 3 },
    ];
  }, []);

  // Prepare line type options
  const lineTypeOptions = useMemo(() => {
    return (['estimates', 'predictions', 'reported'] as const).map((type) => ({
      id: type,
      label: LINE_TYPE_LABELS[type] || type,
      value: type,
    }));
  }, []);

  // Prepare month options (YTD at top, then Jan through Dec in order)
  const monthOptions = useMemo(() => {
    const options = [{ id: 'ytd', label: 'YTD', value: null as number | null }];
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const isCurrentYear = selectedYear === currentYear;
    const maxMonth = isCurrentYear ? currentMonth : 12;

    for (let i = 1; i <= maxMonth; i++) {
      options.push({
        id: i.toString(),
        label: monthNames[i - 1],
        value: i,
      });
    }

    return options;
  }, [selectedYear, currentYear, currentMonth]);

  // Validation mutation
  const validateMutation = useValidateProfitabilityData();

  // Upload mutation
  const uploadMutation = useUploadProfitabilityData();

  // Fetch report data
  const {
    data: report,
    isLoading,
    error,
    refetch,
  } = useProfitabilityReport({
    year: selectedYear,
    dimensions: confirmedDimensions,
    month: selectedMonth,
    lineType: selectedLineType,
    divisionIds:
      selectedDivisions.length > 0
        ? selectedDivisions.map((d) => d.id)
        : undefined,
    supplierIds:
      selectedSuppliers.length > 0
        ? selectedSuppliers.map((s) => s.id)
        : undefined,
    productFamilyDescs:
      selectedProductFamilyDescs.length > 0
        ? selectedProductFamilyDescs.map((p) => p.value)
        : undefined,
    shipTypes:
      selectedShipTypes.length > 0
        ? selectedShipTypes.map((s) => s.value)
        : undefined,
  });

  const dimensionsChanged =
    pendingDimensions[0] !== confirmedDimensions[0] ||
    pendingDimensions[1] !== confirmedDimensions[1] ||
    pendingDimensions[2] !== confirmedDimensions[2];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setPendingDimensions((prev) => {
        const oldIndex = prev.indexOf(active.id as ProfitabilityDimension);
        const newIndex = prev.indexOf(over.id as ProfitabilityDimension);
        return arrayMove(prev, oldIndex, newIndex) as DimensionTuple;
      });
    }
  }, []);

  const handleApplyDimensions = useCallback(() => {
    setConfirmedDimensions([...pendingDimensions] as DimensionTuple);
  }, [pendingDimensions]);

  /**
   * Perform the actual upload, optionally skipping unmatched rows
   */
  const performUpload = async (file: File, skipUnmatched: boolean) => {
    setUploadProgress([
      {
        documentId: `temp-${Date.now()}`,
        fileName: file.name,
        progress: 50,
        status: UploadStatus.uploading,
      },
    ]);

    try {
      const result = await uploadMutation.mutateAsync({
        file,
        skipUnmatched,
        year: uploadYear,
      });
      if (result.data?.success) {
        setUploadProgress([
          {
            documentId: `temp-${Date.now()}`,
            fileName: file.name,
            progress: 100,
            status: UploadStatus.uploaded,
          },
        ]);
        const skippedMsg =
          result.data.rowsSkipped && result.data.rowsSkipped > 0
            ? ` Skipped: ${result.data.rowsSkipped}.`
            : '';
        showToast({
          title: 'Upload Successful',
          message: `Processed ${result.data.rowsProcessed} rows. Inserted: ${result.data.rowsInserted}, Updated: ${result.data.rowsUpdated}.${skippedMsg}`,
          color: 'success',
        });
        setIsUploadModalOpen(false);
        setUploadProgress([]);
        setValidationResult(null);
        setPendingFile(null);
        await refetch();
      } else if (result.data && !result.data.success) {
        setUploadProgress([
          {
            documentId: `temp-${Date.now()}`,
            fileName: file.name,
            progress: 0,
            status: UploadStatus.failed,
            error: 'Upload failed',
          },
        ]);
        setUploadErrors(result.data.errors || []);
      }
    } catch (err: unknown) {
      console.error('Upload error:', err);
      setUploadProgress([
        {
          documentId: `temp-${Date.now()}`,
          fileName: file.name,
          progress: 0,
          status: UploadStatus.failed,
          error: err instanceof Error ? err.message : 'Upload failed',
        },
      ]);

      const apiError = err as {
        responseData?: { data?: SupplierSalesMarginUploadResponse };
      };
      if (apiError.responseData?.data?.errors) {
        setUploadErrors(apiError.responseData.data.errors);
      } else if (
        uploadMutation.data?.data &&
        !uploadMutation.data.data.success
      ) {
        setUploadErrors(uploadMutation.data.data.errors || []);
      } else {
        setUploadErrors([
          {
            row: 0,
            message:
              err instanceof Error
                ? err.message
                : 'Upload failed. Please check the file format and try again.',
          },
        ]);
      }
    }
  };

  const profitabilityExportFilters = useMemo(() => {
    const filters: Record<string, unknown> = {
      year: selectedYear,
      dimensions: confirmedDimensions.join(','),
      month: selectedMonth,
      lineType: selectedLineType,
    };
    if (selectedDivisions.length > 0) {
      filters.divisionIds = selectedDivisions.map((d) => d.id).join(',');
    }
    if (selectedSuppliers.length > 0) {
      filters.supplierIds = selectedSuppliers.map((s) => s.id).join(',');
    }
    if (selectedProductFamilyDescs.length > 0) {
      filters.productFamilyDescs = selectedProductFamilyDescs
        .map((p) => p.value)
        .join('|');
    }
    if (selectedShipTypes.length > 0) {
      filters.shipTypes = selectedShipTypes.map((s) => s.value).join(',');
    }
    return filters;
  }, [
    selectedYear,
    confirmedDimensions,
    selectedMonth,
    selectedLineType,
    selectedDivisions,
    selectedSuppliers,
    selectedProductFamilyDescs,
    selectedShipTypes,
  ]);

  const runProfitabilityDownload = useCallback(
    async (label: 'CSV' | 'Excel', action: () => Promise<void>) => {
      try {
        await action();
        showToast({
          message: `${label} downloaded successfully`,
          title: 'Success',
          color: 'success',
          icon: 'check',
        });
      } catch (error) {
        console.error(`Failed to download ${label}:`, error);
        showToast({
          message:
            error instanceof Error
              ? error.message
              : `Failed to download ${label}`,
          title: 'Error',
          color: 'error',
          icon: 'error',
        });
      }
    },
    [showToast],
  );

  const handleDownloadCsv = useCallback(async () => {
    await runProfitabilityDownload('CSV', () =>
      downloadProfitabilityReportCsv(profitabilityExportFilters),
    );
  }, [profitabilityExportFilters, runProfitabilityDownload]);

  const handleDownloadXlsx = useCallback(async () => {
    await runProfitabilityDownload('Excel', async () => {
      const csv = await fetchProfitabilityReportCsv(profitabilityExportFilters);
      await downloadRebateCsvStringXlsx(csv, 'profitability-report.csv');
    });
  }, [profitabilityExportFilters, runProfitabilityDownload]);

  /**
   * Handle delete confirmation
   */
  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      const result = await deleteSalesMarginsForYear(selectedYear);
      if (result.success) {
        showToast({
          message: `Deleted ${result.deletedCount} sales margin record(s) for ${selectedYear}`,
          title: 'Success',
          color: 'success',
          icon: 'check',
        });
        await refetch();
      }
    } catch (error) {
      console.error('Failed to delete sales margins:', error);
      showToast({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to delete sales margins',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  }, [selectedYear, refetch, showToast]);

  /**
   * Handle file selection
   */
  const handleFilesSelected = async (files: File[]) => {
    if (files.length === 0) {
      return;
    }
    if (files.length > 1) {
      showToast({
        title: 'Error',
        message: 'Please upload only one file at a time.',
        color: 'error',
      });
      return;
    }

    const file = files[0];
    setUploadErrors([]);
    setValidationResult(null);
    setPendingFile(null);
    setUploadProgress([
      {
        documentId: `temp-${Date.now()}`,
        fileName: file.name,
        progress: 0,
        status: UploadStatus.uploading,
      },
    ]);

    try {
      const validateResult = await validateMutation.mutateAsync({ file });

      if (validateResult.data?.valid) {
        await performUpload(file, false);
      } else if (validateResult.data) {
        setValidationResult(validateResult.data);
        setPendingFile(file);
        setIsConfirmDialogOpen(true);
        setUploadProgress([]);
      }
    } catch (err: unknown) {
      console.error('Validation error:', err);
      setUploadProgress([
        {
          documentId: `temp-${Date.now()}`,
          fileName: file.name,
          progress: 0,
          status: UploadStatus.failed,
          error: err instanceof Error ? err.message : 'Validation failed',
        },
      ]);
      setUploadErrors([
        {
          row: 0,
          message:
            err instanceof Error
              ? err.message
              : 'Failed to validate file. Please check the file format and try again.',
        },
      ]);
    }
  };

  const handleConfirmUpload = async () => {
    setIsConfirmDialogOpen(false);
    if (pendingFile) {
      await performUpload(pendingFile, true);
    }
  };

  const handleCancelUpload = () => {
    setIsConfirmDialogOpen(false);
    setValidationResult(null);
    setPendingFile(null);
    setUploadProgress([]);
  };

  // TreeTable columns
  const treeTableColumns = useMemo((): ColumnDef<ProfitabilityReportRow>[] => {
    return [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 280,
        cell: ({ row }) => {
          const data = row.original;
          const label = data.dimensionLabel || '';
          return <span className="font-medium">{label}</span>;
        },
      },
      {
        accessorKey: 'internalCode',
        header: 'Internal Codes',
        size: 100,
        cell: ({ getValue }) => {
          const value = getValue() as string | undefined;
          if (!value) return <span className="text-muted-foreground">-</span>;
          return <span>{value}</span>;
        },
      },
      {
        accessorKey: 'cogs',
        header: 'COGS ($)',
        size: 100,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatCurrencyWhole(value)}</span>;
        },
      },
      {
        accessorKey: 'landedMarginPercent',
        header: 'Landed Margin (%)',
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatPercent(value)}</span>;
        },
      },
      ...(showDollarAmounts
        ? [
            {
              accessorKey: 'nonConditional' as const,
              header: 'Non-Conditional ($)',
              size: 130,
              cell: ({ getValue }: { getValue: () => unknown }) => {
                const value = getValue() as number;
                return <span>{formatCurrencyWhole(value)}</span>;
              },
            },
          ]
        : []),
      {
        accessorKey: 'nonConditionalPercent',
        header: 'Non-Conditional (%)',
        size: 130,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatPercent(value)}</span>;
        },
      },
      ...(showDollarAmounts
        ? [
            {
              accessorKey: 'conditional' as const,
              header: 'Conditional ($)',
              size: 110,
              cell: ({ getValue }: { getValue: () => unknown }) => {
                const value = getValue() as number;
                return <span>{formatCurrencyWhole(value)}</span>;
              },
            },
          ]
        : []),
      {
        accessorKey: 'conditionalPercent',
        header: 'Conditional (%)',
        size: 110,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatPercent(value)}</span>;
        },
      },
      ...(showDollarAmounts
        ? [
            {
              accessorKey: 'redistribution' as const,
              header: 'Redistribution ($)',
              size: 120,
              cell: ({ getValue }: { getValue: () => unknown }) => {
                const value = getValue() as number;
                return <span>{formatCurrencyWhole(value)}</span>;
              },
            },
          ]
        : []),
      {
        accessorKey: 'redistributionPercent',
        header: 'Redistribution (%)',
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatPercent(value)}</span>;
        },
      },
      {
        accessorKey: 'totalRebates',
        header: 'Total Rebates ($)',
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatCurrencyWhole(value)}</span>;
        },
      },
      {
        accessorKey: 'totalRebatesPercent',
        header: 'Total Rebates (%)',
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span>{formatPercent(value)}</span>;
        },
      },
      {
        accessorKey: 'totalProfitPercent',
        header: 'Total Profit (%)',
        size: 110,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return <span className="font-semibold">{formatPercent(value)}</span>;
        },
      },
    ];
  }, [showDollarAmounts]);

  // Column options for the sort dropdown
  const columnHeaders = useMemo(
    () =>
      [
        'Internal Codes',
        'COGS ($)',
        'Landed Margin (%)',
        showDollarAmounts ? 'Non-Conditional ($)' : null,
        'Non-Conditional (%)',
        showDollarAmounts ? 'Conditional ($)' : null,
        'Conditional (%)',
        showDollarAmounts ? 'Redistribution ($)' : null,
        'Redistribution (%)',
        'Total Rebates ($)',
        'Total Rebates (%)',
        'Total Profit (%)',
      ].filter((h): h is string => h !== null),
    [showDollarAmounts],
  );

  // Map column header names to row property names for sorting
  const COLUMN_TO_PROPERTY_MAP: Record<string, keyof ProfitabilityReportRow> =
    useMemo(
      () => ({
        'Internal Codes': 'internalCode',
        'COGS ($)': 'cogs',
        'Landed Margin (%)': 'landedMarginPercent',
        'Non-Conditional ($)': 'nonConditional',
        'Non-Conditional (%)': 'nonConditionalPercent',
        'Conditional ($)': 'conditional',
        'Conditional (%)': 'conditionalPercent',
        'Redistribution ($)': 'redistribution',
        'Redistribution (%)': 'redistributionPercent',
        'Total Rebates ($)': 'totalRebates',
        'Total Rebates (%)': 'totalRebatesPercent',
        'Total Profit (%)': 'totalProfitPercent',
      }),
      [],
    );

  const columnOptions = useMemo(() => {
    const rowLabelOption = {
      id: '__row_label__',
      label: 'Name',
      value: '__row_label__',
    };

    const metricOptions = columnHeaders.map((col) => ({
      id: col,
      label: col,
      value: col,
    }));

    return [rowLabelOption, ...metricOptions];
  }, [columnHeaders]);

  // Group supplier rows under parent groups when supplier is the first dimension
  const groupedRows = useMemo(
    () => (report ? groupSupplierRows(report.rows, confirmedDimensions) : []),
    [report, confirmedDimensions],
  );

  // Sort report rows by selected column
  const sortedReportRows = useMemo(() => {
    if (groupedRows.length === 0) {
      return [];
    }
    if (!selectedColumn) {
      return groupedRows;
    }

    // Helper to sort children recursively
    const sortChildrenRecursively = (
      rows: ProfitabilityReportRow[],
      level: number,
      compareFn: (
        a: ProfitabilityReportRow,
        b: ProfitabilityReportRow,
      ) => number,
    ): ProfitabilityReportRow[] => {
      const sorted = [...rows].sort(compareFn);
      return sorted.map((row) => {
        if (row.children && row.children.length > 0 && level < 3) {
          return {
            ...row,
            children: sortChildrenRecursively(
              row.children,
              level + 1,
              compareFn,
            ),
          };
        }
        return row;
      });
    };

    // Handle alphabetical sort by row label
    if (selectedColumn === '__row_label__') {
      const sortLevel = (
        rows: ProfitabilityReportRow[],
        level: number,
      ): ProfitabilityReportRow[] => {
        const sorted = [...rows].sort((a, b) => {
          const aLabel = a.dimensionLabel || '';
          const bLabel = b.dimensionLabel || '';
          return aLabel.localeCompare(bLabel);
        });
        return sorted.map((row) => {
          if (row.children && row.children.length > 0 && level < 3) {
            return {
              ...row,
              children: sortLevel(row.children, level + 1),
            };
          }
          return row;
        });
      };
      return sortLevel(groupedRows, 0);
    }

    const propertyName = COLUMN_TO_PROPERTY_MAP[selectedColumn];
    if (!propertyName) {
      return groupedRows;
    }

    const compare = (a: ProfitabilityReportRow, b: ProfitabilityReportRow) => {
      const aValue = a[propertyName];
      const bValue = b[propertyName];
      if (typeof aValue === 'string' || typeof bValue === 'string') {
        return String(aValue ?? '').localeCompare(String(bValue ?? ''));
      }
      const aNum = typeof aValue === 'number' ? aValue : 0;
      const bNum = typeof bValue === 'number' ? bValue : 0;
      return bNum - aNum;
    };

    return sortChildrenRecursively(groupedRows, 0, compare);
  }, [groupedRows, selectedColumn, COLUMN_TO_PROPERTY_MAP]);

  // Add unique compound IDs to rows for TreeTable
  type RowWithUniqueId = ProfitabilityReportRow & { _uniqueId: string };

  const getDimensionKey = useCallback((row: ProfitabilityReportRow): string => {
    if (row.dimensionType === 'supplier') {
      return row.supplierId ?? row.dimensionLabel ?? 'unknown';
    }
    if (row.dimensionType === 'division') {
      return row.divisionId ?? row.dimensionLabel ?? 'unknown';
    }
    if (row.dimensionType === 'productFamily') {
      return row.productFamilyDesc ?? row.dimensionLabel ?? 'unknown';
    }
    return row.dimensionLabel ?? 'unknown';
  }, []);

  const rowsWithUniqueIds = useMemo((): RowWithUniqueId[] => {
    function assignUniqueIds(
      rows: ProfitabilityReportRow[],
      prefix: string,
      depth: number,
    ): RowWithUniqueId[] {
      return rows.map((row) => {
        const id = `${prefix}dim${depth}-${getDimensionKey(row)}`;
        const children = row.children
          ? assignUniqueIds(row.children, `${id}__`, depth + 1)
          : undefined;
        return { ...row, _uniqueId: id, children };
      });
    }
    return assignUniqueIds(sortedReportRows, '', 0);
  }, [sortedReportRows, getDimensionKey]);

  // Filter tree rows by name (dimensionLabel) across all levels
  const filteredRows = useMemo((): RowWithUniqueId[] => {
    const query = debouncedNameFilter.trim().toLowerCase();
    if (!query) return rowsWithUniqueIds;

    function filterTree(rows: RowWithUniqueId[]): RowWithUniqueId[] {
      const result: RowWithUniqueId[] = [];
      for (const row of rows) {
        const label = (row.dimensionLabel ?? '').toLowerCase();
        const selfMatches = label.includes(query);

        const filteredChildren = row.children
          ? filterTree(row.children as RowWithUniqueId[])
          : undefined;

        if (selfMatches) {
          result.push(row);
        } else if (filteredChildren && filteredChildren.length > 0) {
          result.push({ ...row, children: filteredChildren });
        }
      }
      return result;
    }

    return filterTree(rowsWithUniqueIds);
  }, [rowsWithUniqueIds, debouncedNameFilter]);

  return (
    <>
      <div className={isRestrictedUser ? '' : 'p-1'}>
        {!isRestrictedUser && (
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <HeaderPageTitle>{REPORT_TITLE}</HeaderPageTitle>
              <p className="text-sm text-muted-foreground">
                <T>
                  Upload supplier sales margin data and view profitability
                  analysis
                </T>
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <ShareNetworkIcon className="h-4 w-4 mr-2" />
                  <T context="button label to share the report">Share</T>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                  <ShareNetworkIcon className="h-4 w-4 mr-2" />
                  <T context="menu item to create a shareable link">
                    Create share link
                  </T>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLinksManagerOpen(true)}>
                  <LinkIcon className="h-4 w-4 mr-2" />
                  <T context="menu item to manage shared links">
                    Manage shared links
                  </T>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Upload, Download, and Delete Buttons */}
        <div className="mb-2 flex gap-2">
          {!isRestrictedUser && (
            <Button
              onClick={() => {
                setUploadYear(selectedYear);
                setIsUploadModalOpen(true);
              }}
            >
              <UploadIcon className="h-4 w-4 mr-2" />
              <T context="button label to upload sales margin data">
                Upload Sales Margin Data
              </T>
            </Button>
          )}
          <Button onClick={handleDownloadCsv} variant="outline">
            <FileCsvIcon className="h-4 w-4 mr-2" />
            <T context="button label to download report as CSV">Download CSV</T>
          </Button>
          <Button onClick={() => void handleDownloadXlsx()} variant="outline">
            <FileXlsIcon className="h-4 w-4 mr-2" />
            <T context="button label to download report as Excel">
              Download Excel
            </T>
          </Button>
          {!isRestrictedUser && (
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              <T context="button label to delete sales margin data for a year">
                Delete <Var>{selectedYear}</Var> Data
              </T>
            </Button>
          )}
        </div>

        {/* Upload Modal */}
        <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                <T>Upload Sales Margin Data</T>
              </DialogTitle>
              <DialogDescription>
                <T>
                  Upload an Excel file (.xlsx) containing supplier sales margin
                  data. The file should have a header row starting with
                  &apos;Corporate Supplier1 Number&apos;.
                </T>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <AlertContainer className="border-red-200 bg-red-50">
                <WarningIcon className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-800">
                  <T>Data Replacement Warning</T>
                </AlertTitle>
                <AlertDescription className="text-red-700">
                  <T>
                    Uploading data for <Var>{uploadYear}</Var> will replace all
                    existing sales margin data for that year. This action cannot
                    be undone.
                  </T>
                </AlertDescription>
              </AlertContainer>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">
                  <T context="label for year selection">Year:</T>
                </label>
                <SingleSelect
                  options={yearOptions}
                  value={
                    yearOptions.find((opt) => opt.value === uploadYear) || null
                  }
                  onChange={(option) => {
                    if (option) {
                      setUploadYear(option.value);
                    }
                  }}
                  placeholder={gt('Select year', {
                    context: 'placeholder for year selector',
                  })}
                  shouldAutoSort={false}
                />
              </div>
              <FileUploadDropzone
                onFilesSelected={handleFilesSelected}
                uploadProgress={uploadProgress}
                isUploading={
                  uploadMutation.isLoading || validateMutation.isLoading
                }
                maxFiles={1}
                fillAvailableHeight={false}
              />

              {/* Errors Table */}
              {uploadErrors.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-lg font-semibold mb-2 text-destructive">
                    <T context="heading showing upload error count">
                      Upload Errors (<Var>{uploadErrors.length}</Var>)
                    </T>
                  </h3>
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>
                            <T context="table column header for row number">
                              Row
                            </T>
                          </TableHead>
                          <TableHead>
                            <T context="table column header for error message">
                              Error Message
                            </T>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uploadErrors.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">
                              {error.row}
                            </TableCell>
                            <TableCell className="text-destructive">
                              {error.message}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsUploadModalOpen(false);
                  setUploadErrors([]);
                  setUploadProgress([]);
                  setUploadYear(selectedYear);
                }}
              >
                <T context="button label to close the upload dialog">Close</T>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmation Dialog for Unmatched Suppliers */}
        <Dialog
          open={isConfirmDialogOpen}
          onOpenChange={setIsConfirmDialogOpen}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                <T>Unmatched Supplier Codes Found</T>
              </DialogTitle>
              <DialogDescription>
                <T>
                  Some rows in your file have supplier codes that don&apos;t
                  match any existing suppliers. You can continue and skip these
                  rows, or cancel to fix the file.
                </T>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {validationResult && (
                <>
                  <div className="text-sm space-y-1">
                    <p>
                      <strong>
                        <T context="label for total row count">Total rows:</T>
                      </strong>{' '}
                      {validationResult.totalRows}
                    </p>
                    <p>
                      <strong>
                        <T context="label for matched rows count">
                          Rows that will be processed:
                        </T>
                      </strong>{' '}
                      {validationResult.matchedRows}
                    </p>
                    <p>
                      <strong>
                        <T context="label for skipped rows count">
                          Rows that will be skipped:
                        </T>
                      </strong>{' '}
                      {validationResult.totalRows -
                        validationResult.matchedRows}
                    </p>
                  </div>

                  {validationResult.unmatchedSupplierCodes.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-sm mb-2">
                        <T context="heading showing unmatched supplier codes count">
                          Unmatched Supplier Codes (
                          <Var>
                            {validationResult.unmatchedSupplierCodes.length}
                          </Var>
                          )
                        </T>
                      </h4>
                      <div className="border rounded-md max-h-48 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>
                                <T context="table column header for supplier code">
                                  Supplier Code
                                </T>
                              </TableHead>
                              <TableHead>
                                <T context="table column header for rows affected count">
                                  Rows Affected
                                </T>
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {validationResult.unmatchedSupplierCodes.map(
                              (item, index) => (
                                <TableRow key={index}>
                                  <TableCell className="font-mono text-sm">
                                    {item.code}
                                  </TableCell>
                                  <TableCell>{item.rowCount}</TableCell>
                                </TableRow>
                              ),
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {validationResult.unmatchedDivisions.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-sm mb-2">
                        <T context="heading showing unmatched divisions count">
                          Unmatched Divisions (
                          <Var>
                            {validationResult.unmatchedDivisions.length}
                          </Var>
                          )
                        </T>
                      </h4>
                      <div className="border rounded-md max-h-48 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>
                                <T context="table column header for division name">
                                  Division Name
                                </T>
                              </TableHead>
                              <TableHead>
                                <T context="table column header for rows affected count">
                                  Rows Affected
                                </T>
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {validationResult.unmatchedDivisions.map(
                              (item, index) => (
                                <TableRow key={index}>
                                  <TableCell>{item.name}</TableCell>
                                  <TableCell>{item.rowCount}</TableCell>
                                </TableRow>
                              ),
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={handleCancelUpload}
                disabled={uploadMutation.isLoading}
              >
                <T context="button label to cancel the upload">Cancel</T>
              </Button>
              <Button
                onClick={handleConfirmUpload}
                disabled={uploadMutation.isLoading}
              >
                {uploadMutation.isLoading ? (
                  <>
                    <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                    <T context="button label while upload is in progress">
                      Uploading...
                    </T>
                  </>
                ) : (
                  <T context="button label to continue upload skipping unmatched rows">
                    Continue and Skip Unmatched
                  </T>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Date Range Status Card */}
        {report?.dateRange && !isLoading && !error && (
          <Card className="mb-2 bg-blue-50 border-blue-200">
            <CardContent className="text-center p-4">
              <div className="text-xs font-bold text-muted-foreground mb-2">
                <T>Supplier Sales Margins Date Range</T>
              </div>
              <div className="text-lg font-bold text-black mb-1">
                {formatDateRange(
                  report.dateRange.minYearMonth,
                  report.dateRange.maxYearMonth,
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                <T>Uploaded data coverage</T>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Report Section */}
        {isLoading ? (
          <Card>
            <CardHeader>
              <CardTitle>
                <T>Profitability Report</T>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TableSkeleton />
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardHeader>
              <CardTitle>
                <T>Profitability Report</T>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-destructive">
                <T>
                  Error loading report: <Var>{error.message}</Var>
                </T>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>
                <T>Profitability Report</T>
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => setIsFullscreen(true)}
                title={gt('Fullscreen', {
                  context: 'button tooltip to enter fullscreen mode',
                })}
              >
                <ArrowsOutIcon className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Dimension Ordering */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">
                  <T context="label for dimension grouping selector">
                    Group by:
                  </T>
                </span>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={pendingDimensions}
                    strategy={horizontalListSortingStrategy}
                  >
                    <div className="flex items-center gap-2">
                      {pendingDimensions.map((dim) => (
                        <SortableDimensionChip key={dim} id={dim} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                {dimensionsChanged && (
                  <Button
                    size="sm"
                    variant="default"
                    onClick={handleApplyDimensions}
                  >
                    <T context="button label to apply dimension changes">
                      Apply
                    </T>
                  </Button>
                )}
              </div>

              {/* Filters */}
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    <T context="filter label for year">Year:</T>
                  </label>
                  <SingleSelect
                    options={yearOptions}
                    value={
                      yearOptions.find((opt) => opt.value === selectedYear) ||
                      null
                    }
                    onChange={(option) => {
                      if (option) {
                        setSelectedYear(option.value);
                      }
                    }}
                    placeholder={gt('Select year', {
                      context: 'placeholder for year selector',
                    })}
                    shouldAutoSort={false}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    <T context="filter label for month">Month:</T>
                  </label>
                  <SingleSelect
                    options={monthOptions}
                    value={
                      monthOptions.find((opt) => opt.value === selectedMonth) ||
                      monthOptions[0]
                    }
                    onChange={(option) => {
                      if (option) {
                        setSelectedMonth(option.value);
                      }
                    }}
                    placeholder={gt('Select month', {
                      context: 'placeholder for month selector',
                    })}
                    shouldAutoSort={false}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    <T context="filter label for line type">Line Type:</T>
                  </label>
                  <div>
                    <SingleSelect
                      options={lineTypeOptions}
                      value={
                        lineTypeOptions.find(
                          (opt) => opt.id === selectedLineType,
                        ) || null
                      }
                      onChange={(option) => {
                        if (option) {
                          setSelectedLineType(option.id as ReportLineType);
                        }
                      }}
                      placeholder={gt('Select line type...', {
                        context: 'placeholder for line type selector',
                      })}
                    />
                    {selectedLineType && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {LINE_TYPE_DESCRIPTIONS[selectedLineType]}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    <T context="filter label for divisions">Divisions:</T>
                  </label>
                  <MultiSelect
                    options={divisionOptions}
                    value={selectedDivisions}
                    onChange={setSelectedDivisions}
                    placeholder={gt('All divisions', {
                      context: 'placeholder when no divisions are selected',
                    })}
                    disabled={divisionsLoading}
                    hideChips={true}
                    liveUpdate={false}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    <T context="filter label for suppliers">Suppliers:</T>
                  </label>
                  <MultiSelect
                    options={supplierOptions}
                    value={selectedSuppliers}
                    onChange={setSelectedSuppliers}
                    placeholder={gt('All suppliers', {
                      context: 'placeholder when no suppliers are selected',
                    })}
                    disabled={suppliersLoading}
                    hideChips={true}
                    liveUpdate={false}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    <T context="filter label for product families">
                      Product Family:
                    </T>
                  </label>
                  <MultiSelect
                    options={productFamilyDescOptions}
                    value={selectedProductFamilyDescs}
                    onChange={setSelectedProductFamilyDescs}
                    placeholder={gt('All product families', {
                      context:
                        'placeholder when no product families are selected',
                    })}
                    disabled={prodFamilyDescsLoading}
                    hideChips={true}
                    liveUpdate={false}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    <T context="filter label for ship types">Ship Type:</T>
                  </label>
                  <MultiSelect
                    options={shipTypeOptions}
                    value={selectedShipTypes}
                    onChange={setSelectedShipTypes}
                    placeholder={gt('All ship types', {
                      context: 'placeholder when no ship types are selected',
                    })}
                    hideChips={true}
                    liveUpdate={false}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium leading-10">
                    <T context="filter label for sort order">Sort By:</T>
                  </label>
                  <SingleSelect
                    options={columnOptions}
                    value={
                      columnOptions.find(
                        (opt) => opt.value === selectedColumn,
                      ) || null
                    }
                    onChange={(option) => {
                      setSelectedColumn(option?.value || null);
                    }}
                    placeholder={gt('No sorting', {
                      context: 'placeholder when no sort column is selected',
                    })}
                    allowClear={true}
                    shouldAutoSort={false}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="dollar-toggle"
                    className="text-sm font-medium leading-10"
                  >
                    <T context="toggle label to show dollar amounts">Show $:</T>
                  </label>
                  <Switch
                    id="dollar-toggle"
                    checked={showDollarAmounts}
                    onCheckedChange={setShowDollarAmounts}
                  />
                </div>
              </div>

              {/* Name Search */}
              <div className="relative w-[300px]">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder={gt('Filter by name...', {
                    context:
                      'search input placeholder for filtering profitability report rows by name',
                  })}
                  className="pl-8 h-9"
                />
              </div>

              {/* Data Truncation Notice */}
              {report?.dateRange &&
                (() => {
                  const maxMonth = parseInt(
                    report.dateRange.maxYearMonth.split('-')[1],
                    10,
                  );
                  const expectedMonth =
                    selectedMonth ??
                    (selectedYear < currentYear ? 12 : currentMonth);
                  if (maxMonth < expectedMonth) {
                    const monthNames = Array.from({ length: 12 }, (_, i) =>
                      new Date(2000, i, 1).toLocaleDateString([], {
                        month: 'short',
                      }),
                    );
                    return (
                      <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                        <InfoIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <div className="text-sm text-blue-700">
                          <T>
                            Sales margin data only covers through{' '}
                            <Var>
                              <span className="font-medium">
                                {monthNames[maxMonth - 1]} {selectedYear}
                              </span>
                            </Var>
                            . Rebate data has been aligned to this range. Months{' '}
                            <Var>{monthNames[maxMonth]}</Var>&ndash;
                            <Var>{monthNames[expectedMonth - 1]}</Var> are not
                            included.
                          </T>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

              {/* Data Quality Notice */}
              {report?.unmatchedBreakdowns &&
                report.unmatchedBreakdowns.length > 0 && (
                  <DataQualityNotice
                    unmatchedBreakdowns={report.unmatchedBreakdowns}
                    isAdmin={!isRestrictedUser}
                  />
                )}

              {/* Report Table */}
              {filteredRows.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  {debouncedNameFilter ? (
                    <T context="message when search filter returns no results">
                      No rows match your search.
                    </T>
                  ) : (
                    <T context="message when no data is available for selected filters">
                      No data available for the selected filters. Please upload
                      sales margin data or adjust your filters.
                    </T>
                  )}
                </div>
              ) : (
                <TreeTable
                  key={`tree-${showDollarAmounts}-${confirmedDimensions.join('-')}-${!!debouncedNameFilter}`}
                  data={filteredRows}
                  columns={treeTableColumns}
                  getRowId={(row) => (row as RowWithUniqueId)._uniqueId}
                  maxHeight={700}
                  defaultExpanded={!!debouncedNameFilter}
                  indentSize={24}
                />
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title={gt('Delete {year} Sales Margins', {
          context:
            'confirm dialog title for deleting sales margin data for a year',
          year: String(selectedYear),
        })}
        message={gt(
          'Are you sure you want to delete all sales margin data for {year}? This action cannot be undone.',
          {
            context: 'confirm dialog message for deleting sales margin data',
            year: String(selectedYear),
          },
        )}
        confirmText={gt('Delete', {
          context: 'confirm button in delete sales margins dialog',
        })}
        cancelText={gt('Cancel', {
          context: 'cancel button in delete sales margins dialog',
        })}
        loading={isDeleting}
        dangerous
      />

      <ShareReportModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
        hideFilters={true}
      />
      <SharedLinksManager
        open={linksManagerOpen}
        onClose={() => setLinksManagerOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
        onCreateNew={() => {
          setLinksManagerOpen(false);
          setShareModalOpen(true);
        }}
      />

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <div
          className="fixed inset-0 bg-background flex flex-col"
          style={{ zIndex: 2000 }}
        >
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h2 className="text-lg font-semibold">
              <T>Profitability Report</T>
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsFullscreen(false)}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 min-h-0">
            {filteredRows.length === 0 ? (
              <div className="text-sm text-muted-foreground p-4">
                {nameFilter ? (
                  <T context="message when search filter returns no results">
                    No rows match your search.
                  </T>
                ) : (
                  <T context="message when no data is available for selected filters">
                    No data available for the selected filters.
                  </T>
                )}
              </div>
            ) : (
              <TreeTable
                key={`tree-fs-${showDollarAmounts}-${confirmedDimensions.join('-')}-${!!debouncedNameFilter}`}
                data={filteredRows}
                columns={treeTableColumns}
                getRowId={(row) => (row as RowWithUniqueId)._uniqueId}
                maxHeight="100%"
                defaultExpanded={!!debouncedNameFilter}
                indentSize={24}
                className="h-full rounded-none border-0"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
